import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const statusConfig = {
  active: { label: "Active", color: "var(--color-success)", bg: "rgba(34,197,94,0.12)" },
  suspended: { label: "Suspended", color: "var(--color-error)", bg: "rgba(239,68,68,0.12)" },
  inactive: { label: "Inactive", color: "var(--color-warning)", bg: "rgba(234,179,8,0.12)" },
};

const emptyForm = {
  full_name: "",
  gym_name: "",
  email: "",
  phone: "",
  city: "",
  password: "",
  confirm_password: "",
  status: "active",
  plan_id: "",
  billing_cycle: "monthly",
};

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deletingVendor, setDeletingVendor] = useState(null);

  // Form state
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/super-admin/vendors');
      setVendors(data || []);
      setMemberCounts({});
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/super-admin/plans');
      setPlans(data || []);
    } catch (err) {
      console.error("Error fetching plans:", err);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchPlans();
  }, []);

  const getVendorStatus = (vendor) => {
    if (!vendor?.is_active) return "suspended";
    return "active";
  };

  const openAddModal = () => {
    setEditingVendor(null);
    setForm(emptyForm);
    setFormError(null);
    setShowFormModal(true);
  };

  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setForm({
      full_name: vendor?.full_name || "",
      gym_name: vendor?.gym_name || "",
      email: vendor?.email || "",
      phone: vendor?.phone || "",
      city: vendor?.city || "",
      password: "",
      confirm_password: "",
      status: getVendorStatus(vendor),
      plan_id: "",
      billing_cycle: "monthly",
    });
    setFormError(null);
    setShowFormModal(true);
  };

  const openDeleteModal = (vendor) => {
    setDeletingVendor(vendor);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSuspend = async (vendor) => {
    try {
      const newActive = vendor?.is_active ? false : true;
      await api.patch(`/super-admin/vendors/${vendor?.id}/status`, { is_active: newActive });
      setVendors((prev) =>
        prev?.map((v) => (v?.id === vendor?.id ? { ...v, is_active: newActive } : v))
      );
    } catch (err) {
      console.error("Error toggling vendor status:", err);
      setError(err?.message);
    }
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);

    // Validation
    if (!form?.full_name?.trim()) return setFormError("Full name is required.");
    if (!form?.email?.trim()) return setFormError("Email is required.");
    if (!editingVendor) {
      if (!form?.password) return setFormError("Password is required for new vendors.");
      if (form?.password?.length < 6) return setFormError("Password must be at least 6 characters.");
      if (form?.password !== form?.confirm_password) return setFormError("Passwords do not match.");
    }

    setFormLoading(true);
    try {
      if (!editingVendor) {
        await api.post('/super-admin/vendors', {
          email: form?.email?.trim(),
          password: form?.password,
          full_name: form?.full_name?.trim(),
          gym_name: form?.gym_name?.trim(),
          phone: form?.phone?.trim(),
          city: form?.city?.trim(),
        });
      } else {
        await api.patch(`/super-admin/vendors/${editingVendor?.id}/status`, {
          is_active: form?.status === "active",
        });
      }

      await fetchVendors();
      setShowFormModal(false);
    } catch (err) {
      console.error("Form submit error:", err);
      setFormError(err?.message || "An error occurred. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingVendor) return;
    setFormLoading(true);
    try {
      await api.delete(`/super-admin/vendors/${deletingVendor?.id}`);
      setVendors((prev) => prev?.filter((v) => v?.id !== deletingVendor?.id));
      setShowDeleteModal(false);
      setDeletingVendor(null);
    } catch (err) {
      console.error("Delete error:", err);
      setError(err?.message);
    } finally {
      setFormLoading(false);
    }
  };

  const filteredVendors = vendors?.filter((v) => {
    const matchSearch =
      v?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      v?.gym_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      v?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
      v?.city?.toLowerCase()?.includes(search?.toLowerCase());
    const status = getVendorStatus(v);
    const matchStatus = filterStatus === "all" || status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-base font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Vendor Management Roster
            </h2>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
              {filteredVendors?.length} vendor{filteredVendors?.length !== 1 ? "s" : ""} · manage accounts, status & access
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            {/* Search */}
            <div className="relative">
              <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="pl-7 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-44"
              />
            </div>
            <button
              onClick={openAddModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
            >
              <Icon name="UserPlus" size={13} color="white" />
              Add Vendor
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
            <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-[var(--color-muted-foreground)]">
              <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
              <span className="text-sm font-[var(--font-caption)]">Loading vendors...</span>
            </div>
          ) : filteredVendors?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                <Icon name="Building2" size={22} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                {search || filterStatus !== "all" ? "No vendors match your filters." : "No vendors yet. Add your first vendor."}
              </p>
              {!search && filterStatus === "all" && (
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
                >
                  <Icon name="Plus" size={13} color="white" />
                  Add First Vendor
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
                  {["Vendor", "Gym / Business", "Email", "Phone", "City", "Members", "Status", "Actions"]?.map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filteredVendors?.map((vendor) => {
                  const status = getVendorStatus(vendor);
                  const cfg = statusConfig?.[status];
                  const memberCount = memberCounts?.[vendor?.id] || 0;
                  return (
                    <tr key={vendor?.id} className="hover:bg-[var(--color-muted)]/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[var(--color-primary)]">
                              {vendor?.full_name?.charAt(0)?.toUpperCase() || "V"}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-body)]">
                              {vendor?.full_name || "—"}
                            </p>
                            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                              Joined {vendor?.created_at ? new Date(vendor.created_at)?.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--color-foreground)] font-[var(--font-body)]">
                        {vendor?.gym_name || <span className="text-[var(--color-muted-foreground)]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        {vendor?.email}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        {vendor?.phone || <span className="text-[var(--color-muted-foreground)]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        {vendor?.city || <span className="text-[var(--color-muted-foreground)]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-xs font-semibold font-[var(--font-data)]">
                          <Icon name="Users" size={10} color="currentColor" />
                          {memberCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold font-[var(--font-caption)]"
                          style={{ color: cfg?.color, backgroundColor: cfg?.bg }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg?.color }}></span>
                          {cfg?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditModal(vendor)}
                            title="Edit vendor"
                            className="p-1.5 rounded-md hover:bg-[var(--color-primary)]/10 transition-colors"
                          >
                            <Icon name="Pencil" size={13} color="var(--color-primary)" />
                          </button>
                          <button
                            onClick={() => handleSuspend(vendor)}
                            title={vendor?.is_active ? "Suspend vendor" : "Activate vendor"}
                            className={`p-1.5 rounded-md transition-colors ${
                              vendor?.is_active
                                ? "hover:bg-[var(--color-warning)]/10"
                                : "hover:bg-[var(--color-success)]/10"
                            }`}
                          >
                            <Icon
                              name={vendor?.is_active ? "PauseCircle" : "PlayCircle"}
                              size={13}
                              color={vendor?.is_active ? "var(--color-warning)" : "var(--color-success)"}
                            />
                          </button>
                          <button
                            onClick={() => openDeleteModal(vendor)}
                            title="Delete vendor"
                            className="p-1.5 rounded-md hover:bg-[var(--color-error)]/10 transition-colors"
                          >
                            <Icon name="Trash2" size={13} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* Add / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-[var(--color-primary)]/15 flex items-center justify-center">
                  <Icon name={editingVendor ? "Pencil" : "UserPlus"} size={15} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    {editingVendor ? "Edit Vendor" : "Add New Vendor"}
                  </h3>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                    {editingVendor ? "Update vendor details and status" : "Create a vendor account with login access"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
              >
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="p-5 space-y-4">
              {formError && (
                <div className="p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
                  <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{formError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Full Name */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    Full Name <span className="text-[var(--color-error)]">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={form?.full_name}
                    onChange={handleFormChange}
                    placeholder="e.g. John Smith"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>

                {/* Gym / Business Name */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    Gym / Business Name
                  </label>
                  <input
                    type="text"
                    name="gym_name"
                    value={form?.gym_name}
                    onChange={handleFormChange}
                    placeholder="e.g. Iron Paradise Gym"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    Email <span className="text-[var(--color-error)]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form?.email}
                    onChange={handleFormChange}
                    placeholder="vendor@example.com"
                    disabled={!!editingVendor}
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {editingVendor && (
                    <p className="text-xs text-[var(--color-muted-foreground)] mt-1 font-[var(--font-caption)]">Email cannot be changed after creation.</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form?.phone}
                    onChange={handleFormChange}
                    placeholder="+1 555 000 0000"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form?.city}
                    onChange={handleFormChange}
                    placeholder="e.g. New York"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                    Status
                  </label>
                  <select
                    name="status"
                    value={form?.status}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Password fields — only for new vendors */}
                {!editingVendor && (
                  <>
                    <div>
                      <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        Password <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={form?.password}
                        onChange={handleFormChange}
                        placeholder="Min. 6 characters"
                        className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        Confirm Password <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <input
                        type="password"
                        name="confirm_password"
                        value={form?.confirm_password}
                        onChange={handleFormChange}
                        placeholder="Re-enter password"
                        className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                      />
                    </div>

                    {/* Plan Assignment — only for new vendors */}
                    <div className="sm:col-span-2 pt-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-[var(--color-border)]"></div>
                        <span className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] px-2">Plan Assignment</span>
                        <div className="h-px flex-1 bg-[var(--color-border)]"></div>
                      </div>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-3">
                        Optionally assign a paid plan now. If left blank, vendor gets a 14-day free trial automatically.
                      </p>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        Subscription Plan
                      </label>
                      <select
                        name="plan_id"
                        value={form?.plan_id}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                      >
                        <option value="">— Auto-assign 14-day trial —</option>
                        {plans?.map((p) => (
                          <option key={p?.id} value={p?.id}>
                            {p?.plan_name} (${p?.price_monthly}/mo · ${p?.price_yearly}/yr)
                          </option>
                        ))}
                      </select>
                    </div>

                    {form?.plan_id && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                          Billing Cycle
                        </label>
                        <select
                          name="billing_cycle"
                          value={form?.billing_cycle}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly (Save ~17%)</option>
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--color-border)]">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formLoading ? (
                    <>
                      <Icon name="Loader2" size={13} color="white" className="animate-spin" />
                      {editingVendor ? "Saving..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      <Icon name={editingVendor ? "Save" : "UserPlus"} size={13} color="white" />
                      {editingVendor ? "Save Changes" : "Create Vendor"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] w-full max-w-sm">
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[var(--color-error)]/15 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={18} color="var(--color-error)" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Delete Vendor
                  </h3>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-[var(--color-foreground)]">{deletingVendor?.full_name}</span>
                    {deletingVendor?.gym_name ? ` (${deletingVendor?.gym_name})` : ""}? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeletingVendor(null); }}
                  className="px-4 py-2 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={formLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-error)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formLoading ? (
                    <>
                      <Icon name="Loader2" size={13} color="white" className="animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Icon name="Trash2" size={13} color="white" />
                      Delete Vendor
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
